import { useForm, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { z } from 'zod';
import { useMemo, useState, type SubmitEventHandler } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Flex, Heading, Text, TextField } from '@radix-ui/themes';
import {
  Button,
  FieldError,
  PasswordInput,
  Separator,
  ServerErrorCallout,
} from '@/components/common';
import { GoogleIcon } from '@/components/icons';
import { WizardHeader, WizardScreen } from '@/components/layout';
import { analyticsClient } from '@/lib';
import { isAuthError } from '@/lib/auth';
import { authService } from '@/services';
import { PENDING_BIRTHDAY_KEY } from './BirthdayPage';
import { fieldHintStyle, fieldLabelStyle } from './styles';

type Translate = (key: string) => string;

const createSignupEmailSchema = (t: Translate) =>
  z
    .object({
      email: z
        .string()
        .min(1, t('errors.emailRequired'))
        .check(z.email(t('errors.emailInvalid'))),
      password: z.string().min(8, t('errors.passwordTooShort')),
      confirmPassword: z.string().min(1, t('errors.passwordRequired')),
    })
    .refine(data => data.password === data.confirmPassword, {
      message: t('errors.passwordMismatch'),
      path: ['confirmPassword'],
    });

type SignupEmailFormValues = z.infer<ReturnType<typeof createSignupEmailSchema>>;

const SignupEmailForm = ({
  emailLabel,
  emailPlaceholder,
  emailHint,
  passwordLabel,
  passwordPlaceholder,
  confirmPasswordLabel,
  confirmPasswordPlaceholder,
  register,
  errors,
  onSubmit,
}: {
  emailLabel: string;
  emailPlaceholder: string;
  emailHint: string;
  passwordLabel: string;
  passwordPlaceholder: string;
  confirmPasswordLabel: string;
  confirmPasswordPlaceholder: string;
  register: UseFormRegister<SignupEmailFormValues>;
  errors: FieldErrors<SignupEmailFormValues>;
  onSubmit: SubmitEventHandler<HTMLFormElement>;
}) => (
  <form id="signup-email-form" onSubmit={onSubmit} noValidate>
    <Flex direction="column" gap="3">
      <Flex direction="column">
        <Flex direction="column" gap="1">
          <Text size="1" color="gray" style={fieldLabelStyle}>
            {emailLabel}
          </Text>
          <TextField.Root
            type="email"
            placeholder={emailPlaceholder}
            aria-label={emailLabel}
            data-testid="signup-email"
            aria-invalid={errors.email ? true : undefined}
            {...register('email')}
          />
          <FieldError message={errors.email?.message} />
        </Flex>
        <Text size="1" color="gray" mt="2" style={fieldHintStyle}>
          {emailHint}
        </Text>
      </Flex>

      <Flex direction="column" gap="1">
        <Text size="1" color="gray" style={fieldLabelStyle}>
          {passwordLabel}
        </Text>
        <PasswordInput
          placeholder={passwordPlaceholder}
          aria-label={passwordLabel}
          data-testid="signup-password"
          aria-invalid={errors.password ? true : undefined}
          {...register('password')}
        />
        <FieldError message={errors.password?.message} />
      </Flex>

      <Flex direction="column" gap="1">
        <Text size="1" color="gray" style={fieldLabelStyle}>
          {confirmPasswordLabel}
        </Text>
        <PasswordInput
          placeholder={confirmPasswordPlaceholder}
          aria-label={confirmPasswordLabel}
          data-testid="signup-confirm-password"
          aria-invalid={errors.confirmPassword ? true : undefined}
          showStrengthCheck={false}
          {...register('confirmPassword')}
        />
        <FieldError message={errors.confirmPassword?.message} />
      </Flex>
    </Flex>
  </form>
);

const GoogleButton = ({
  loading,
  label,
  onGoogleSignUp,
}: {
  loading: boolean;
  label: string;
  onGoogleSignUp: () => void;
}) => (
  <Button
    type="button"
    onClick={onGoogleSignUp}
    disabled={loading}
    variant="outline"
    color="gray"
    fullWidth
  >
    <Flex align="center" gap="2" as="span">
      <GoogleIcon />
      {label}
    </Flex>
  </Button>
);

export function SignUpPage() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const schema = useMemo(() => createSignupEmailSchema(key => t(key)), [t]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupEmailFormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: { email: '', password: '', confirmPassword: '' },
  });

  const [birthday] = useState(() => sessionStorage.getItem(PENDING_BIRTHDAY_KEY));
  if (!birthday) {
    return <Navigate to="/birthday" replace />;
  }

  const handleEmailSignup = async (data: SignupEmailFormValues) => {
    setError(null);
    setLoading(true);
    try {
      await authService.signupWithEmailPassword({
        email: data.email,
        password: data.password,
        name: data.email.split('@')[0],
        birthday,
      });
      sessionStorage.removeItem(PENDING_BIRTHDAY_KEY);
      analyticsClient.capture('signup_completed', { method: 'email_password' });
      navigate('/verify-email');
    } catch (err: unknown) {
      console.error(err);
      if (isAuthError(err) && err.code === 'auth/email-already-in-use') {
        setError(t('errors.emailAlreadyInUse'));
      } else {
        setError(err instanceof Error ? err.message : t('errors.registrationFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setError(null);
    setLoading(true);
    try {
      const { user } = await authService.authWithGoogle('signup');
      await authService.updateBirthday(user.uid, birthday);
      sessionStorage.removeItem(PENDING_BIRTHDAY_KEY);
      analyticsClient.capture('signup_completed', { method: 'google' });
      if (user.emailVerified) {
        // Google sign-ups skip the verify-email screen, so mark the funnel step here.
        analyticsClient.capture('email_verified', { method: 'google' });
      }
      navigate(user.emailVerified ? '/' : '/verify-email');
    } catch (err: unknown) {
      console.error('Google Signup Failure:', err);
      if (err instanceof Error) {
        if (err.message === 'auth/popup-blocked') {
          setError(t('errors.popupBlocked'));
        } else {
          setError(err.message);
        }
      } else {
        setError(t('errors.googleSignInFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <WizardScreen header={<WizardHeader onBack={() => navigate('/')} />}>
      <Flex direction="column" gap="4" py="2" pb="5">
        <Heading size="5" weight="light">
          {t('signupEmail.title')}
        </Heading>

        {error ? <ServerErrorCallout message={error} /> : null}

        <SignupEmailForm
          emailLabel={t('signupEmail.emailLabel')}
          emailPlaceholder={t('signupEmail.emailPlaceholder')}
          emailHint={t('signupEmail.emailHint')}
          passwordLabel={t('signupEmail.passwordLabel')}
          passwordPlaceholder={t('signupEmail.passwordPlaceholder')}
          confirmPasswordLabel={t('signupEmail.confirmPasswordLabel')}
          confirmPasswordPlaceholder={t('signupEmail.confirmPasswordPlaceholder')}
          register={register}
          errors={errors}
          onSubmit={handleSubmit(handleEmailSignup)}
        />
        <Button
          type="submit"
          form="signup-email-form"
          disabled={loading}
          color="gray"
          highContrast
          fullWidth
          size="lg"
        >
          {t('signupEmail.continue')}
        </Button>

        <Separator size="4" my="2" />

        <Text size="1" color="gray" align="center">
          {t('actions.divider')}
        </Text>

        <GoogleButton
          loading={loading}
          label={t('actions.google')}
          onGoogleSignUp={() => void handleGoogleSignup()}
        />
      </Flex>
    </WizardScreen>
  );
}
